import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Bed, Users, X } from "@phosphor-icons/react";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";

export const MapSection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [apartments, setApartments] = useState([]);
  const [openSlug, setOpenSlug] = useState(null);
  const mapRef = useRef(null);
  const [mapSize, setMapSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    api.get("/apartments").then((r) => setApartments(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const measure = () => {
      if (mapRef.current) setMapSize({ w: mapRef.current.offsetWidth, h: mapRef.current.offsetHeight });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const openApt = apartments.find((a) => a.slug === openSlug);

  // Posición del popup en píxeles para que nunca se salga del mapa
  const CARD_W = 220, CARD_H = 240;
  let popupStyle = null;
  let popupAbove = false;
  if (openApt && mapSize.w) {
    const px = Math.min(Math.max((openApt.map_x / 100) * mapSize.w, CARD_W / 2 + 8), mapSize.w - CARD_W / 2 - 8);
    const markerY = (openApt.map_y / 100) * mapSize.h;
    popupAbove = markerY > CARD_H + 24;
    const py = popupAbove ? markerY - 16 : markerY + 16;
    popupStyle = { left: `${px}px`, top: `${py}px` };
  }

  return (
    <section id="mapa" data-testid="map-section" className="py-24 md:py-36 bg-[#F5F1EA] overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <div className="md:flex items-end justify-between border-b border-line pb-10 mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.35em] text-terracotta mb-4">{t("map.kicker")}</p>
              <h2 className="font-serif text-4xl md:text-6xl text-ink leading-[1.02]">{t("map.title")}</h2>
            </div>
            <p className="text-ink/55 font-light mt-6 md:mt-0 max-w-xs text-base">{t("map.sub")}</p>
          </div>
        </Reveal>

        <div className="max-w-[980px] mx-auto">
          <Reveal>
              <div
                data-testid="cadiz-map"
                className="border border-line bg-white p-2 md:p-3"
                style={{ perspective: "1400px" }}
              >
                <div
                  ref={mapRef}
                  className="relative overflow-hidden"
                  style={{ boxShadow: "0 34px 60px -18px rgba(26,29,26,0.4)" }}
                >
                  <img src="/uploads/mapa-cadiz-mute.png" alt="Mapa de Cádiz" className="w-full h-auto block" />

                  {apartments.map((apt, i) =>
                    apt.map_x != null ? (
                      <div
                        key={apt.slug}
                        className="absolute z-10 -translate-x-1/2 -translate-y-1/2"
                        style={{ left: `${apt.map_x}%`, top: `${apt.map_y}%` }}
                      >
                        <motion.button
                          data-testid={`map-marker-${apt.slug}`}
                          initial={{ scale: 0, opacity: 0 }}
                          whileInView={{ scale: 1, opacity: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: 0.4 + i * 0.12, type: "spring", stiffness: 300, damping: 18 }}
                          whileHover={{ scale: 1.2 }}
                          onClick={() => setOpenSlug(openSlug === apt.slug ? null : apt.slug)}
                          aria-label={apt.name}
                        >
                          <span
                            className="map-marker"
                            style={{ background: apt.color, boxShadow: openSlug === apt.slug ? "0 0 0 6px rgba(200,90,50,.3)" : undefined }}
                          >
                            {i + 1}
                          </span>
                        </motion.button>
                      </div>
                    ) : null
                  )}

                  <AnimatePresence>
                    {openApt && popupStyle && (
                      <div
                        className={`absolute z-20 -translate-x-1/2 hidden md:block ${popupAbove ? "-translate-y-full" : ""}`}
                        style={popupStyle}
                      >
                        <motion.div
                          key={openApt.slug}
                          data-testid="map-info-card"
                          initial={{ opacity: 0, y: 12, scale: 0.96 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 12, scale: 0.96 }}
                          transition={{ duration: 0.25 }}
                          onClick={() => navigate(`/apartamento/${openApt.slug}`)}
                          className="w-[220px] bg-white border border-line shadow-2xl cursor-pointer"
                        >
                          <img src={openApt.photos[0]} alt={openApt.name} className="w-full h-28 object-cover" />
                          <div className="p-4">
                            <button
                              data-testid="map-info-close"
                              onClick={(e) => { e.stopPropagation(); setOpenSlug(null); }}
                              className="absolute top-2 right-2 w-7 h-7 bg-ink/60 text-sand flex items-center justify-center hover:bg-terracotta transition-colors"
                              aria-label="Cerrar"
                            >
                              <X size={13} />
                            </button>
                            <p className="font-serif text-xl text-ink m-0">{openApt.name}</p>
                            <p className="text-[10px] uppercase tracking-[0.18em] text-ink/50 mt-1">{openApt.zone}</p>
                            <p className="flex items-center gap-3 text-[11px] text-ink/55 mt-2">
                              <span className="flex items-center gap-1"><Bed size={12} /> {openApt.bedrooms} {t("detail.bedrooms")}</span>
                              <span className="flex items-center gap-1"><Users size={12} /> {openApt.guests} {t("detail.guests")}</span>
                            </p>
                            <div className="flex items-center justify-between mt-3 pt-3 border-t border-line">
                              <span className="font-serif text-lg text-ink">
                                {openApt.price_per_night} €<span className="text-xs text-ink/50 font-sans"> / {t("gallery.night")}</span>
                              </span>
                              <Link
                                to={`/apartamento/${openApt.slug}`}
                                data-testid="map-info-link"
                                onClick={(e) => e.stopPropagation()}
                                className="text-[10px] uppercase tracking-[0.15em] text-terracotta font-medium hover:text-atlantic transition-colors"
                              >
                                {t("search.book")} →
                              </Link>
                            </div>
                          </div>
                        </motion.div>
                      </div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
          </Reveal>

          {/* En móvil el popup flotante se recortaba: la tarjeta va debajo del mapa */}
          <AnimatePresence>
            {openApt && (
              <motion.div
                key={openApt.slug + "-movil"}
                data-testid="map-info-card-mobile"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 12 }}
                transition={{ duration: 0.25 }}
                onClick={() => navigate(`/apartamento/${openApt.slug}`)}
                className="md:hidden relative mt-4 grid grid-cols-[110px_1fr] bg-white border border-terracotta shadow-xl cursor-pointer"
              >
                <div className="h-full min-h-[110px] overflow-hidden">
                  <img src={openApt.photos[0]} alt={openApt.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-4">
                  <button
                    data-testid="map-info-close-mobile"
                    onClick={(e) => { e.stopPropagation(); setOpenSlug(null); }}
                    className="absolute top-2 right-2 w-7 h-7 bg-ink/60 text-sand flex items-center justify-center"
                    aria-label="Cerrar"
                  >
                    <X size={13} />
                  </button>
                  <p className="font-serif text-xl text-ink m-0">{openApt.name}</p>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-ink/50 mt-1">{openApt.zone}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-serif text-lg text-ink">{openApt.price_per_night} €<span className="text-xs text-ink/50 font-sans"> / {t("gallery.night")}</span></span>
                    <span className="text-[10px] uppercase tracking-[0.15em] text-terracotta font-medium">{t("search.book")} →</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
